import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import jsQR from 'jsqr';
import { api } from '../lib/api.js';
import { toast } from '../lib/toast.js';

interface Worker {
  id: string;
  name: string;
  role: string;
  companyRef?: string | null;
  photo?: string | null;
}

const QR_DARK = '#E8A427';
const QR_LIGHT = '#161D2E';
const QR_SIZE = 100;

export function QrScanner() {
  const navigate = useNavigate();
  const [manualInput, setManualInput] = useState('');
  const [result, setResult] = useState<{ worker: Worker | null; rawData: string } | null>(null);
  const [active, setActive] = useState(false);
  const [status, setStatus] = useState<{ text: string; tone: 'idle' | 'ok' | 'err' }>({ text: 'Scanner inactive', tone: 'idle' });
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const lastHitRef = useRef<{ id: string; at: number } | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);

  const { data: workers = [] } = useQuery({ queryKey: ['workers'], queryFn: () => api.get<Worker[]>('/workers').then((r) => r.data) });

  const resolveWorker = (raw: string): Worker | null => {
    const token = raw.trim();
    if (!token) return null;
    const upper = token.toUpperCase();
    const lower = token.toLowerCase();
    let hit = workers.find((w) => w.companyRef && w.companyRef.toUpperCase() === upper);
    if (!hit) hit = workers.find((w) => w.id === token);
    if (!hit) {
      const uuidLike = token.match(/[0-9a-fA-F-]{8,}/)?.[0];
      if (uuidLike) hit = workers.find((w) => w.id === uuidLike || w.id.startsWith(uuidLike));
    }
    if (!hit) hit = workers.find((w) => w.name.toLowerCase() === lower);
    return hit ?? null;
  };

  const showResult = (raw: string) => {
    const found = resolveWorker(raw);
    setResult({ worker: found, rawData: raw });
    if (found) {
      setStatus({ text: `Found: ${found.name}`, tone: 'ok' });
      toast.success('Worker scanned', found.name);
    } else {
      setStatus({ text: `No match for "${raw}"`, tone: 'err' });
    }
    return !!found;
  };

  const processManual = () => {
    const v = manualInput.trim();
    if (!v) return;
    if (!showResult(v)) toast.error('Not found', v);
    setManualInput('');
  };

  const stop = () => {
    if (rafRef.current !== null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    lastHitRef.current = null;
    setActive(false);
    setStatus({ text: 'Scanner stopped', tone: 'idle' });
  };

  const start = async () => {
    if (active) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error('Camera unavailable', 'getUserMedia not supported');
      setStatus({ text: 'Camera not supported', tone: 'err' });
      return;
    }
    setStatus({ text: 'Requesting camera access…', tone: 'idle' });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
      streamRef.current = stream;
      const vid = videoRef.current;
      if (!vid) { stream.getTracks().forEach((t) => t.stop()); return; }
      vid.srcObject = stream;
      await vid.play();
      setActive(true);
      setStatus({ text: 'Scanning… Point camera at a QR code', tone: 'ok' });
      tick();
    } catch (err: any) {
      toast.error('Camera access denied', err?.message ?? 'Allow camera in browser settings');
      setStatus({ text: `Camera error: ${err?.message ?? 'blocked'}`, tone: 'err' });
    }
  };

  const tick = () => {
    const vid = videoRef.current;
    const cvs = canvasRef.current;
    if (!vid || !cvs) { rafRef.current = requestAnimationFrame(tick); return; }
    if (vid.readyState >= 2) {
      cvs.width = vid.videoWidth;
      cvs.height = vid.videoHeight;
      const ctx = cvs.getContext('2d');
      if (ctx && cvs.width > 0 && cvs.height > 0) {
        ctx.drawImage(vid, 0, 0, cvs.width, cvs.height);
        const img = ctx.getImageData(0, 0, cvs.width, cvs.height);
        const code = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' });
        if (code?.data) {
          const now = Date.now();
          const last = lastHitRef.current;
          if (!last || last.id !== code.data || now - last.at > 2500) {
            lastHitRef.current = { id: code.data, at: now };
            showResult(code.data);
            stop();
            return;
          }
        }
      }
    }
    rafRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => () => stop(), []);

  const generateQRsClick = () => {
    gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    toast.info('QR codes ready', `${workers.length} worker${workers.length !== 1 ? 's' : ''} — scroll down to print or save`);
  };

  const openProfile = (id: string) => navigate(`/workers?focus=${id}`);
  const openSignIn = (id: string) => navigate(`/signin?worker=${id}`);

  return (
    <div className="page" id="page-qrscan">
      <div className="hbar">
        <div>
          <div className="ph">⬛ QR Code Scanner</div>
          <div className="ps">Scan a worker QR code to open their profile, or sign in/out of a project</div>
        </div>
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={start}>📷 Start Camera</button>
          <button className="btn btn-ghost" onClick={generateQRsClick}>⬇ Generate QR Codes</button>
        </div>
      </div>
      <div className="g2">
        <div className="card">
          <div className="sec-title" style={{ marginBottom: 10 }}>Camera Scanner</div>
          <div id="qr-video-wrap" style={{ background: '#000', borderRadius: 10, overflow: 'hidden', position: 'relative', aspectRatio: '4 / 3', maxHeight: 360, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <video
              ref={videoRef}
              id="qr-video"
              muted
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: active ? 'block' : 'none' }}
            />
            <canvas ref={canvasRef} id="qr-canvas" style={{ display: 'none' }} />
            {!active ? (
              <div id="qr-placeholder" style={{ color: '#666', textAlign: 'center', padding: 30, position: 'absolute' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>📷</div>
                <div style={{ fontSize: 13 }}>Click "Start Camera" to activate scanner</div>
              </div>
            ) : (
              <div id="qr-overlay" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 200, height: 200, border: '3px solid var(--accent)', borderRadius: 10, boxShadow: '0 0 0 2000px rgba(0,0,0,.4)' }} />
              </div>
            )}
          </div>
          <div id="qr-status" style={{ marginTop: 10, textAlign: 'center', fontSize: 12, color: 'var(--muted)' }}>
            {status.tone === 'ok' ? <span className="geo-ok" style={{ color: 'var(--live)' }}>● </span> : null}
            {status.tone === 'err' ? <span className="geo-err" style={{ color: 'var(--danger)' }}>● </span> : null}
            <span style={status.tone === 'err' ? { color: 'var(--danger)' } : undefined}>{status.text}</span>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <button
              className="btn btn-primary btn-sm"
              id="qr-start-btn"
              style={{ flex: 1, display: active ? 'none' : undefined, justifyContent: 'center' }}
              onClick={start}
            >
              📷 Start
            </button>
            <button
              className="btn btn-ghost btn-sm"
              id="qr-stop-btn"
              style={{ flex: 1, display: active ? undefined : 'none', justifyContent: 'center' }}
              onClick={stop}
            >
              ⏹ Stop
            </button>
          </div>
          <div style={{ marginTop: 12, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: 10 }}>
            <div className="sec-title" style={{ marginBottom: 7 }}>Or enter worker ID manually</div>
            <div style={{ display: 'flex', gap: 7 }}>
              <input
                type="text"
                className="form-input"
                id="qr-manual-input"
                placeholder="Worker ID or CLS-W001…"
                value={manualInput}
                onChange={(e) => setManualInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') processManual(); }}
                style={{ flex: 1 }}
              />
              <button className="btn btn-primary btn-sm" onClick={processManual}>Go</button>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="sec-title" style={{ marginBottom: 10 }}>Scan Result</div>
          <div id="qr-result" style={{ minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 12, textAlign: 'center' }}>
            {result?.worker ? (
              <ScanResultCard worker={result.worker} onOpenProfile={() => openProfile(result.worker!.id)} onSignIn={() => openSignIn(result.worker!.id)} />
            ) : result ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 28, marginBottom: 8, color: 'var(--danger)' }}>✗</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--danger)' }}>Worker not found</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 5 }}>QR data: "{result.rawData}"</div>
                <div style={{ fontSize: 11, color: 'var(--faint)', marginTop: 4 }}>Check worker has a company reference matching this QR code</div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 28, marginBottom: 8 }}>⬛</div>
                Scan a QR code to see the result here
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="card" style={{ marginTop: 14 }} ref={gridRef}>
        <div className="sec-title" style={{ marginBottom: 10 }}>Generate Worker QR Codes</div>
        <div id="qr-codes-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
          {workers.map((w) => (
            <div key={w.id} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: 12, textAlign: 'center' }}>
              <QrTile value={w.companyRef || w.id} />
              <div style={{ fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{w.name}</div>
              <div style={{ fontSize: 9, color: 'var(--muted)' }}>{w.companyRef || `ID:${w.id.slice(0, 8)}`}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ScanResultCard({ worker, onOpenProfile, onSignIn }: { worker: Worker; onOpenProfile: () => void; onSignIn: () => void }) {
  const initials = worker.name.split(' ').map((n) => n[0] ?? '').join('').slice(0, 2).toUpperCase();
  return (
    <div style={{ textAlign: 'left', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, padding: 12, background: 'var(--live-dim)', border: '1px solid rgba(34,197,94,.3)', borderRadius: 10 }}>
        {worker.photo ? (
          <img src={worker.photo} alt="" style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--live)' }} />
        ) : (
          <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 18, color: '#000' }}>{initials}</div>
        )}
        <div>
          <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 16, color: 'var(--live)' }}>✓ Worker Identified</div>
          <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>{worker.name}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{worker.role} · {worker.companyRef || `ID:${worker.id.slice(0, 8)}`}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={onOpenProfile}>👤 Open Profile</button>
        <button className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center' }} onClick={onSignIn}>✅ Sign In/Out</button>
      </div>
    </div>
  );
}

function QrTile({ value }: { value: string }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { default: QRCode } = await import('qrcode');
      if (!canvasRef.current || cancelled) return;
      QRCode.toCanvas(canvasRef.current, value, { errorCorrectionLevel: 'M', margin: 1, width: QR_SIZE, color: { dark: QR_DARK, light: QR_LIGHT } }, () => undefined);
    })();
    return () => { cancelled = true; };
  }, [value]);
  return (
    <canvas ref={canvasRef} style={{ width: QR_SIZE, height: QR_SIZE, margin: '0 auto 8px', display: 'block' }} />
  );
}
